import React from 'react';
import { Layout, Space, Badge, Dropdown, Avatar, Button, Tooltip, message, Modal, Form, Input, Tag, Descriptions, Spin } from 'antd';
import {
  BellOutlined,
  GlobalOutlined,
  UserOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
  LoginOutlined,
  PoweroffOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { useTheme } from '../context/ThemeContext';
import api from '../api';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const { t, setLocale } = useLocale();
  const { isDark, toggleTheme } = useTheme();
  const [langTooltipOpen, setLangTooltipOpen] = React.useState(false);
  const [checkedIn, setCheckedIn] = React.useState(false);
  const [checkLoading, setCheckLoading] = React.useState(false);

  const [profileVisible, setProfileVisible] = React.useState(false);
  const [profileData, setProfileData] = React.useState<any>(null);
  const [profileLoading, setProfileLoading] = React.useState(false);
  const [pwdVisible, setPwdVisible] = React.useState(false);
  const [pwdLoading, setPwdLoading] = React.useState(false);
  const [pwdForm] = Form.useForm();

  React.useEffect(() => {
    api.get('/attendance/today')
      .then((res: any) => { if (res?.code === 0 && res.data?.check_in) setCheckedIn(true); })
      .catch(() => {});
  }, []);

  const openProfile = async () => {
    if (!user?.id) return;
    setProfileVisible(true);
    setProfileLoading(true);
    setPwdVisible(false);
    pwdForm.resetFields();
    try {
      const res: any = await api.get(`/users/${user.id}`);
      if (res?.code === 0) {
        setProfileData(res.data);
      } else {
        message.error(res?.message || t('加载失败'));
      }
    } catch (e: any) {
      message.error(e?.message || t('加载失败'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePwdSubmit = async (values: any) => {
    setPwdLoading(true);
    try {
      const res: any = await api.post('/auth/change-password', {
        old_password: values.old_password,
        new_password: values.new_password,
      });
      if (res?.code === 0) {
        message.success(t('密码修改成功'));
        setPwdVisible(false);
        pwdForm.resetFields();
      } else {
        message.error(res?.message || t('密码修改失败'));
      }
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || t('密码修改失败');
      message.error(msg);
    } finally {
      setPwdLoading(false);
    }
  };

  const handleCheck = async () => {
    setCheckLoading(true);
    try {
      const action = checkedIn ? 'check-out' : 'check-in';
      const res = await api.post('/attendance/' + action);
      if (res.code === 0) {
        message.success(checkedIn ? t('签出成功') : t('签到成功'));
        setCheckedIn(!checkedIn);
      } else {
        message.error(res.message || '操作失败');
      }
    } catch (e: any) {
      message.error(e?.message || '操作失败');
    } finally { setCheckLoading(false); }
  };

  const userMenuItems = [
    { key: 'profile', icon: <UserOutlined />, label: t('个人资料') },
    { type: 'divider' },
    checkedIn
      ? { key: 'check-out', icon: <PoweroffOutlined style={{ color: '#faad14' }} />, label: <span style={{ color: '#faad14' }}>{t('签退')}</span> }
      : { key: 'check-in', icon: <LoginOutlined style={{ color: '#52c41a' }} />, label: <span style={{ color: '#52c41a' }}>{t('签到')}</span> },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: t('退出登录'), danger: true },
  ];

  const langMenuItems = [
    { key: 'zh', label: t('中文'), onClick: () => setLocale('zh') },
    { key: 'en', label: 'English', onClick: () => setLocale('en') },
  ];

  const handleUserMenu = ({ key }: { key: string }) => {
    if (key === 'logout') logout();
    if (key === 'profile') openProfile();
    if (key === 'check-in' || key === 'check-out') handleCheck();
  };

  return (
    <Layout.Header
      style={{
        background: isDark ? '#141414' : '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        borderBottom: `1px solid ${isDark ? '#303030' : '#f0f0f0'}`,
        position: 'sticky',
        top: 0,
        height: 56,
        zIndex: 100,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}
    >
      <Space size={16}>
        <Tooltip title={t('主题')}>
          <Button size="middle"
            type="text"
            icon={isDark ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
            style={{ color: isDark ? '#fff' : '#595959' }}
          />
        </Tooltip>
        <Tooltip title={t('语言')} open={langTooltipOpen} onOpenChange={setLangTooltipOpen}>
          <Dropdown
            menu={{ items: langMenuItems }}
            placement="bottomRight"
            onOpenChange={(open) => { if (open) setLangTooltipOpen(false); }}
          >
            <Button size="middle"
              type="text"
              icon={<GlobalOutlined />}
              style={{ color: isDark ? '#fff' : '#595959' }}
            />
          </Dropdown>
        </Tooltip>
        <Badge count={0} size="small">
          <BellOutlined style={{ fontSize: 'var(--font-lg)', color: isDark ? '#fff' : '#595959', cursor: 'pointer' }} />
        </Badge>
        <Dropdown menu={{ items: userMenuItems as any, onClick: handleUserMenu }} placement="bottomRight">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: 6,
              transition: 'background 0.2s',
            }}
            className="header-user-dropdown"
          >
            <Avatar size="small" style={{ background: '#1677ff' }}>
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <span style={{ fontSize: 'var(--font-sm)', color: isDark ? '#fff' : '#333' }} className="desktop-only">
              {user?.username || user?.real_name}
            </span>
          </div>
        </Dropdown>
      </Space>

      {/* 个人资料弹窗 */}
      <Modal
        title={t('个人资料')}
        open={profileVisible}
        onCancel={() => { setProfileVisible(false); setPwdVisible(false); pwdForm.resetFields(); }}
        footer={null}
        width={480}
      >
        {profileLoading ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}><Spin /></div>
        ) : profileData ? (
          <div>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={t('用户名')}>{profileData.username}</Descriptions.Item>
              <Descriptions.Item label={t('姓名')}>{profileData.real_name || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('邮箱')}>{profileData.email || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('角色')}>
                {profileData.roles?.length
                  ? profileData.roles.map((r: any) => <Tag key={r.id} color="blue">{r.name}</Tag>)
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('状态')}>
                <Tag color={profileData.status === 1 ? 'success' : 'error'}>
                  {profileData.status === 1 ? t('启用') : t('禁用')}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('负责门店')}>
                {profileData.stores?.length
                  ? profileData.stores.map((s: any) => (
                      <Tag key={s.id}>{s.store_no ? `[${s.store_no}] ` : ''}{s.name}</Tag>
                    ))
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginTop: 16 }}>
              <Button
                type="link"
                icon={<LockOutlined />}
                style={{ paddingLeft: 0 }}
                onClick={() => { setPwdVisible(!pwdVisible); pwdForm.resetFields(); }}
              >
                {t('修改密码')}
              </Button>
              {pwdVisible && (
                <Form form={pwdForm} layout="vertical" onFinish={handlePwdSubmit} style={{ marginTop: 8 }}>
                  <Form.Item
                    name="old_password"
                    label={t('旧密码')}
                    rules={[{ required: true, message: t('请输入旧密码') }]}
                  >
                    <Input.Password placeholder={t('请输入旧密码')} />
                  </Form.Item>
                  <Form.Item
                    name="new_password"
                    label={t('新密码')}
                    rules={[{ required: true, min: 6, message: t('密码至少6位') }]}
                  >
                    <Input.Password placeholder={t('请输入新密码')} />
                  </Form.Item>
                  <Form.Item
                    name="confirm_password"
                    label={t('确认新密码')}
                    rules={[
                      { required: true, message: t('请确认密码') },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('new_password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t('两次输入的密码不一致')));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder={t('请再次输入新密码')} />
                  </Form.Item>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" loading={pwdLoading}>
                      {t('确认修改')}
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={() => { setPwdVisible(false); pwdForm.resetFields(); }}>
                      {t('取消')}
                    </Button>
                  </Form.Item>
                </Form>
              )}
            </div>
          </div>
        ) : null}
      </Modal>
    </Layout.Header>
  );
};

export default Header;
